/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useRef, useEffect} from 'react'
import {
    Box,
    SimpleGrid,
    useMultiStyleConfig,
    createStylesContext
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useLocation} from 'react-router-dom'
import {useGlobalFooter} from '../../contexts/global-elements-context'
import {useNavigationContext} from '../../contexts/navigation-context'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {getHideStyles} from '../optimizely/utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'

/* Components */
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import NewsletterSignUp from './partial/newsletterSignUp'
import NavigationLinks from './partial/navigationLinks'
import FooterCountrySelector from './partial/footerCountrySelector'
import DownloadTheApp from './partial/downloadTheApp'
import {parseShortcodes} from '../optimizely/gdi/utils/gdi-shortcode-parser'
import {convertCmsStyles} from './utils'
import {useBrand} from '../../contexts/brand-context'

const [StylesProvider] = createStylesContext('Footer')

const Footer = ({onShowCountrySelectorOnFooter = noop, ...otherProps}) => {
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const location = useLocation()
    const {isHomepagePath} = useNavigationContext()
    const [isPDP, setIsPDP] = useState(false)
    const [isChatActive, setIsChatActive] = useState(false)
    const styles = useMultiStyleConfig('Footer', {isPDP, isChatActive})
    const [_showCountrySelectorOnFooter, setShowCountrySelectorOnFooter] = useState(false)
    const {data: footerData} = useGlobalFooter()
    const {isGarage} = useBrand()
    const brandName = isGarage ? 'Garage' : 'Dynamite'
    const footerContainerRef = useRef(null)
    const [hideStyles, setHideStyles] = useState({})
    const isNewsletterEnabled = getSiteEnvVarValue(appConfig, 'SFSC_EMAIL_SUBSCRIPTION_ENABLED', siteId) === 'true'
    const handleShowCountrySelectorOnFooter = (showCSOnFooter) => {
        setShowCountrySelectorOnFooter(showCSOnFooter)
        onShowCountrySelectorOnFooter(showCSOnFooter)
    }
    const isHomepage = isHomepagePath(location?.pathname)
    const cmsBottomBarText = footerData?.bottomBar?.textLeft?.textForWeb
        ? parseShortcodes(footerData.bottomBar.textLeft.textForWeb, {brandName})
        : null
    const cmsBottomBarStyles = convertCmsStyles(footerData?.bottomBar?.textLeft?.stylesPerViewType)

    useEffect(() => {
        const checkChatStatus = () => {
            const chatFrame = document.querySelector('#embeddedMessagingFrame')
            const chatButton = document.querySelector('#embeddedMessagingConversationButton')
            const isFrameVisible =
                chatFrame && window.getComputedStyle(chatFrame).display !== 'none'
            const isButtonVisible =
                chatButton && window.getComputedStyle(chatButton).display !== 'none'
            setIsChatActive(!!(isFrameVisible || isButtonVisible))
        }
        checkChatStatus()
        const observer = new MutationObserver(checkChatStatus)
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class']
        })
        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        const footerContainerEl = footerContainerRef?.current
        const appContainer = footerContainerEl.closest('#app')
        const isPDPPage = appContainer.querySelectorAll('[data-is-pdp]')?.length > 0
        setIsPDP(isPDPPage)
    }, [])

    useEffect(() => {
        setHideStyles(getHideStyles(footerData?.raw?.HideOnDevices))
    }, [footerData])

    if (hideStyles.doNotLoadComponent) return

    return (
        <Box
            ref={footerContainerRef}
            as="footer"
            {...styles.container}
            {...otherProps}
            {...hideStyles}
        >
            <Box {...styles.content} as="section">
                <StylesProvider value={styles}>
                    {isNewsletterEnabled && (
                        <HideOnDesktop>
                            <NewsletterSignUp {...styles.subscribeContent} />
                        </HideOnDesktop>
                    )}

                    {isHomepage && (
                        <HideOnDesktop>
                            <Box 
                                {...styles.downloadContent}
                            >
                                <DownloadTheApp />
                            </Box>
                        </HideOnDesktop>
                    )}

                    <SimpleGrid columns={{base: 1, lg: 3}}>
                        <Box gridColumn={{base: 'span 1', lg: 'span 2'}}>
                            <SimpleGrid
                                columns={{base: 1, lg: 4}}
                                spacing={{lg: 2}}
                                {...styles.linksContent}
                            >
                                <NavigationLinks footerData={footerData} />
                            </SimpleGrid>
                        </Box>
                        <HideOnMobile
                            gridColumn={{base: 'span 1', lg: 'span 1'}}
                            {...styles.subscribeContent}
                            {...styles.subscribeContentDesktop}
                        >
                            {isNewsletterEnabled && (
                                <Box
                                    display={{lg: 'flex'}}
                                    flexDir={{lg: 'column'}}
                                    justifyContent={{lg: 'center'}}
                                    marginBottom={{lg: 6}}
                                >
                                    <NewsletterSignUp />
                                </Box>
                            )}
                            {isHomepage && (
                                <Box
                                    {...styles.downloadContent} 
                                >
                                    <DownloadTheApp />
                                </Box>
                            )}
                        </HideOnMobile>
                    </SimpleGrid>

                    <SimpleGrid columns={{base: 1, lg: 3}}>
                        <Box
                            {...styles.copyrightContent}
                            gridColumn={{
                                base: 'span 1',
                                lg: _showCountrySelectorOnFooter ? 'span 2' : 'span 3'
                            }}
                            order={{base: 1, lg: 0}}
                        >
                            {cmsBottomBarText && (
                                    <Box
                                        {...styles.copyright}
                                        {...cmsBottomBarStyles}
                                        dangerouslySetInnerHTML={{__html: cmsBottomBarText}}
                                    />
                                )}
                        </Box>
                        <Box
                            order={{base: 0, lg: 1}}
                            padding={{base: '24px 16px', lg: '16px 24px'}}
                            display={_showCountrySelectorOnFooter ? 'block' : 'none'}
                            gridColumn={{base: 'span 1', lg: 'span 1'}}
                            borderTop={{base: '1px solid'}}
                            borderLeft={{base: '1px solid'}}
                            borderColor={{base: 'grey.400'}}
                            zIndex={1}
                        >
                            <FooterCountrySelector
                                onShowCountrySelectorOnFooter={handleShowCountrySelectorOnFooter}
                            />
                        </Box>
                    </SimpleGrid>
                </StylesProvider>
            </Box>
        </Box>
    )
}

export default Footer
